import { gql, useMutation, useQuery } from '@apollo/client';
import { CARD_TERMS_VERSION, MANDATE_TEXT_DRAFT, MANDATE_TEXT_VERSION } from '@orn/global/billing/mandateText.js';
import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

import { extractErrorCode } from '../../utils/apollo/error';
import PlaidConnectionModal from '../plaid/PlaidConnectionModal';
import { useAccountLink } from '../plaid/useAccountLink';

import CardAuthorizationBase from './CardAuthorization/CardAuthorizationBase';
import CardEntry from './CardEntry';
import { checkoutErrorMessage } from './checkoutErrors';
import { checkoutReducer, initialCheckoutState } from './checkoutReducer';
import MandateAuthorizationBase from './MandateAuthorization/MandateAuthorizationBase';
import PaymentAccountPickerBase from './PaymentAccountPicker/PaymentAccountPickerBase';
import { preselectAccountId } from './PaymentAccountPicker/preselectAccount';
import { ELIGIBLE_BILLING_ACCOUNT_FRAGMENT, useAccountPicker } from './PaymentAccountPicker/useAccountPicker';
import PlanPickerBase from './PlanPicker/PlanPickerBase';

import type {
  CheckoutDataQuery,
  CheckoutDataQueryVariables,
  SubscribeMutation,
  SubscribeMutationVariables,
  SubscribeWithCardMutation,
  SubscribeWithCardMutationVariables,
} from './__generated__/CheckoutFlow.gql';
import type { SubscriptionInterval } from '../../__generated__/globalTypes';

const CHECKOUT_DATA = gql`
  query CheckoutData {
    eligibleBillingAccounts {
      ...EligibleBillingAccount
    }
    annual: subscriptionOffer(interval: ANNUAL) {
      interval
      amountDisplay
      trialDays
      firstChargeDisplay
      quoteToken
    }
    monthly: subscriptionOffer(interval: MONTHLY) {
      interval
      amountDisplay
      trialDays
      firstChargeDisplay
      quoteToken
    }
  }
  ${ELIGIBLE_BILLING_ACCOUNT_FRAGMENT}
`;

const SUBSCRIBE_WITH_CARD = gql`
  mutation SubscribeWithCard($input: SubscribeWithCardInput!) {
    subscribeWithCard(input: $input) {
      subscriptionId
      status
    }
  }
`;

const SUBSCRIBE = gql`
  mutation Subscribe($input: SubscribeInput!) {
    subscribe(input: $input) {
      subscriptionId
      status
    }
  }
`;

export interface ICheckoutFlowProps {
  /** Called once the subscription exists. */
  onComplete: () => void;
  /**
   * Leaves checkout without subscribing. Hosts that swap this flow in over
   * other content (billing settings) pass it so the household has a way back;
   * the onboarding step omits it, since the wizard chrome owns navigation.
   */
  onExit?: () => void;
}

/**
 * The checkout flow: account, then plan, then authorize on the bank-debit
 * rail. The account screen can divert to card entry, and the card rail
 * rejoins at plan selection with its own authorization copy.
 *
 * The acknowledgment is answered by resubmitting rather than by asking the
 * server first. `subscribe` refuses with `SUBSCRIBE_THIRD_PARTY_ACK_REQUIRED`
 * before it mints a token or reaches Stripe, so the first attempt costs
 * nothing beyond the owner-name check and the refusal is the only reliable
 * signal that the disclosure is needed — a pre-flight query would be a second
 * call and still race the server's own check.
 */
const CheckoutFlow = ({ onComplete, onExit }: ICheckoutFlowProps) => {
  const { data, loading, error, refetch } = useQuery<CheckoutDataQuery, CheckoutDataQueryVariables>(CHECKOUT_DATA);
  // Owned here rather than handed in as a callback: the link flow only works
  // with its modal rendered, and only this component knows to refresh the
  // eligible accounts once a new bank lands.
  const accountLink = useAccountLink({ onLinked: () => void refetch() });
  const [subscribe, { loading: isSubmitting }] = useMutation<SubscribeMutation, SubscribeMutationVariables>(SUBSCRIBE);
  const [subscribeWithCard, { loading: isSubmittingCard }] = useMutation<
    SubscribeWithCardMutation,
    SubscribeWithCardMutationVariables
  >(SUBSCRIBE_WITH_CARD);

  const [state, dispatch] = React.useReducer(checkoutReducer, null, () => initialCheckoutState(null));
  const [requiresThirdPartyAck, setRequiresThirdPartyAck] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  // The reducer clears the ack on an account change; the prompt and refusal
  // clear with it. A same-account reselect invalidates nothing.
  const { accounts, selectedAccount, selectAccount } = useAccountPicker({
    eligibleBillingAccounts: data?.eligibleBillingAccounts,
    loading,
    selectedAccountId: state.selectedAccountId,
    onSelect: (accountId) => dispatch({ type: 'selectAccount', accountId }),
    onAccountChanged: () => {
      setRequiresThirdPartyAck(false);
      setErrorMessage(undefined);
    },
    onSelectionInvalidated: () => dispatch({ type: 'accountRemoved' }),
  });

  // The pre-selection can only be made once the accounts land.
  const preselected = React.useRef(false);
  React.useEffect(() => {
    if (preselected.current || accounts.length === 0) {
      return;
    }
    const accountId = preselectAccountId(accounts);
    if (accountId !== null) {
      preselected.current = true;
      dispatch({ type: 'selectAccount', accountId });
    }
  }, [accounts]);

  const offer = state.selectedInterval === data?.annual.interval ? data?.annual : data?.monthly;

  /**
   * The submit plumbing both rails share. `execute` runs the rail's mutation
   * and reports whether a subscription came back; `onAckRequired` is the bank
   * rail's one extra refusal branch.
   */
  const runSubscribeSubmit = async (
    execute: () => Promise<{ subscribed: boolean; errors: Parameters<typeof extractErrorCode>[0] }>,
    onAckRequired?: () => void
  ) => {
    setErrorMessage(undefined);
    let outcome: Awaited<ReturnType<typeof execute>>;
    try {
      outcome = await execute();
    } catch {
      // errorPolicy covers GraphQL errors only; a transport failure rejects,
      // and the caller discards this promise, so an uncaught rejection here
      // would leave the subscriber with no message at all.
      setErrorMessage(checkoutErrorMessage(ApiErrorCode.INTERNAL_SERVER_ERROR) ?? undefined);
      return;
    }

    if (outcome.subscribed) {
      onComplete();
      return;
    }

    const code = extractErrorCode(outcome.errors);
    if (code === ApiErrorCode.SUBSCRIBE_THIRD_PARTY_ACK_REQUIRED && onAckRequired !== undefined) {
      onAckRequired();
      return;
    }
    if (code === ApiErrorCode.SUBSCRIBE_QUOTE_EXPIRED) {
      // The page sat open long enough that the quoted date lapsed. Refresh
      // the offer so the screen shows the date a resubmit will store.
      void refetch();
    }
    if (code === ApiErrorCode.SUBSCRIBE_ALREADY_SUBSCRIBED) {
      // The subscription exists — most likely this request's own retry. The
      // household got what it asked for, so advance rather than report failure.
      onComplete();
      return;
    }
    setErrorMessage(checkoutErrorMessage(code) ?? undefined);
  };

  const handleSubmit = async () => {
    if (state.selectedAccountId === null || offer == null) {
      return;
    }
    const { selectedAccountId } = state;
    await runSubscribeSubmit(
      async () => {
        const result = await subscribe({
          variables: {
            input: {
              financialAccountId: selectedAccountId,
              interval: state.selectedInterval,
              mandateTextVersion: MANDATE_TEXT_VERSION,
              // Echoed verbatim: the server redeems this signed quote as the
              // mandate evidence and ends the trial on it, so what this screen
              // displayed is what the household is actually on.
              quoteToken: offer.quoteToken,
              thirdPartyAck: state.thirdPartyAck,
            },
          },
          errorPolicy: 'all',
        });
        return { subscribed: result.data?.subscribe != null, errors: result.errors ?? [] };
      },
      // The owner name did not match, or could not be checked. Reveal the
      // disclosure; the subscriber's confirmation is what the resubmit carries.
      () => setRequiresThirdPartyAck(true)
    );
  };

  const handleCardSubmit = async () => {
    if (state.cardSetupIntentId === null || offer == null) {
      return;
    }
    const { cardSetupIntentId } = state;
    await runSubscribeSubmit(async () => {
      const result = await subscribeWithCard({
        variables: {
          input: {
            setupIntentId: cardSetupIntentId,
            interval: state.selectedInterval,
            // Echoed verbatim, as on the bank rail: the server redeems this
            // signed quote as the mandate evidence and ends the trial on it.
            quoteToken: offer.quoteToken,
            mandateTextVersion: CARD_TERMS_VERSION,
          },
        },
        errorPolicy: 'all',
      });
      return { subscribed: result.data?.subscribeWithCard != null, errors: result.errors ?? [] };
    });
  };

  if (error != null && data == null) {
    return (
      <div className="flex w-full flex-col items-center gap-4">
        <p className="text-tertiary text-sm">We couldn't load checkout. Check your connection and try again.</p>
        <Button type="button" size="md" color="secondary" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (loading || data == null) {
    return null;
  }

  let screen: React.ReactNode;
  if (state.screen === 'account') {
    screen = (
      <PaymentAccountPickerBase
        accounts={accounts}
        selectedAccountId={state.selectedAccountId}
        onSelectAccount={selectAccount}
        onContinue={() => {
          // Advancing recommits to the bank rail; a card-rail refusal shown
          // before coming back here means nothing on it.
          setErrorMessage(undefined);
          dispatch({ type: 'advance' });
        }}
        onLinkAnotherBank={accountLink.trigger}
        onPayByCard={() => {
          // A refusal shown for one rail means nothing on the other.
          setErrorMessage(undefined);
          dispatch({ type: 'payByCard' });
        }}
        onCancel={onExit}
      />
    );
  } else if (state.screen === 'card') {
    screen = (
      <CardEntry
        onConfirmed={(setupIntentId) => {
          // A refusal shown for a previous card means nothing for this one.
          setErrorMessage(undefined);
          dispatch({ type: 'cardConfirmed', setupIntentId });
        }}
        onBack={() => dispatch({ type: 'back' })}
      />
    );
  } else if (state.screen === 'plan') {
    screen = (
      <PlanPickerBase
        plans={[data.annual, data.monthly]}
        selectedInterval={state.selectedInterval}
        onSelectInterval={(interval: SubscriptionInterval) => dispatch({ type: 'selectInterval', interval })}
        onContinue={() => dispatch({ type: 'advance' })}
        onBack={() => dispatch({ type: 'back' })}
      />
    );
  } else if (offer == null) {
    screen = null;
  } else if (state.rail === 'card') {
    screen = (
      <CardAuthorizationBase
        amountDisplay={offer.amountDisplay}
        firstChargeDisplay={offer.firstChargeDisplay}
        onSubmit={() => void handleCardSubmit()}
        onBack={() => dispatch({ type: 'back' })}
        isSubmitting={isSubmittingCard}
        errorMessage={errorMessage}
      />
    );
  } else if (selectedAccount === null) {
    screen = null;
  } else {
    screen = (
      <MandateAuthorizationBase
        institution={selectedAccount.bankName}
        mask={selectedAccount.mask}
        amountDisplay={offer.amountDisplay}
        firstChargeDisplay={offer.firstChargeDisplay}
        requiresThirdPartyAck={requiresThirdPartyAck}
        thirdPartyAck={state.thirdPartyAck}
        onThirdPartyAckChange={(acknowledged) => dispatch({ type: 'setThirdPartyAck', acknowledged })}
        onSubmit={() => void handleSubmit()}
        onBack={() => dispatch({ type: 'back' })}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        submitLabel="Start free trial"
        mandateTemplate={MANDATE_TEXT_DRAFT}
        chargeSummary={`$0 today. ${offer.amountDisplay} starting ${offer.firstChargeDisplay}.`}
      />
    );
  }

  return (
    <>
      {screen}
      <PlaidConnectionModal
        isOpen={accountLink.isModalOpen}
        onOpenChange={accountLink.onModalOpenChange}
        onSelect={accountLink.onModalSelect}
        errorCode={accountLink.errorCode}
      />
    </>
  );
};

CheckoutFlow.displayName = 'CheckoutFlow';

export default CheckoutFlow;
